import React, { useEffect, useState } from 'react'
import Helmet from 'react-helmet'
import { useHistory, useLocation } from 'react-router-dom'

import CaseTypesService from '../../services/CaseTypesService'

import './CaseTypePicker.scss'

const CaseTypePicker = () => {
  const history = useHistory()
  const [caseTypes, setCaseTypes] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)
  const [expandedIds, setExpandedIds] = useState(new Set())
  const [selectedCaseType, setSelectedCaseType] = useState(null)

  useEffect(() => {
    CaseTypesService.getAll()
      .then(data => setCaseTypes(data))
      .catch(() => setError('Unable to load case types. Please try again.'))
      .finally(() => setIsLoading(false))
  }, [])

  const location = useLocation()

  const handleSelectCaseType = ct => {
    setSelectedCaseType(ct)
  }

  const handleSelectIntakeMode = mode => {
    if (!selectedCaseType) return
    // Carry the page the user came from so IntakeForm can return there after Smart Intake.
    const from = location.state?.from ?? document.referrer ?? '/'
    const modeQuery = mode === 'ongoing' ? '&mode=ongoing' : ''
    history.push(
      `/case/intake?caseTypeId=${selectedCaseType.id}&caseTypeName=${encodeURIComponent(
        selectedCaseType.name,
      )}${modeQuery}`,
      { from },
    )
  }

  const toggleExpanded = (e, id) => {
    e.stopPropagation() // don't trigger card selection
    setExpandedIds(prev => {
      const next = new Set(prev)
      next.has(id) ? next.delete(id) : next.add(id)
      return next
    })
  }

  // Descriptions longer than this threshold get the clamp + Read more treatment.
  const CLAMP_THRESHOLD = 160

  return (
    <div className="case-type-picker">
      <Helmet title={selectedCaseType ? `New ${selectedCaseType.name} Case` : 'New Case | Select Case Type'} />
      <div className="picker-header">
        <h2 className="picker-title">{selectedCaseType ? `New ${selectedCaseType.name} Case` : 'New Case'}</h2>
        <p className="picker-subtitle">
          {selectedCaseType ? 'How would you like to begin this case?' : 'Select the type of case you are opening'}
        </p>
        {selectedCaseType && (
          <button type="button" className="picker-back-btn mt-2" onClick={() => setSelectedCaseType(null)}>
            <i className="fas fa-arrow-left mr-1" /> Change case type
          </button>
        )}
      </div>

      {isLoading && (
        <div className="d-flex justify-content-center p-5">
          <div className="spinner-border text-primary" role="status">
            <span className="sr-only">Loading…</span>
          </div>
        </div>
      )}

      {error && <div className="alert alert-danger mx-4">{error}</div>}

      {!isLoading && !error && !selectedCaseType && (
        <div className="picker-grid">
          {caseTypes.length === 0 ? (
            <div className="picker-empty">
              <p>No case types have been configured yet.</p>
              <p className="text-muted">An administrator can add case types under Library → Case Types.</p>
            </div>
          ) : (
            caseTypes.map(ct => (
              <button
                key={ct.id}
                id={`caseType-${ct.id}`}
                className="picker-card"
                onClick={() => handleSelectCaseType(ct)}
                type="button"
              >
                <div className="picker-card-icon">
                  <i className="fas fa-folder-open" />
                </div>
                <div className="picker-card-body">
                  <h5 className="picker-card-name">{ct.name}</h5>
                  {ct.description && (
                    <>
                      <p
                        className={[
                          'picker-card-desc',
                          ct.description.length > CLAMP_THRESHOLD && !expandedIds.has(ct.id) ? 'is-clamped' : '',
                        ]
                          .filter(Boolean)
                          .join(' ')}
                      >
                        {ct.description}
                      </p>
                      {ct.description.length > CLAMP_THRESHOLD && (
                        <button
                          type="button"
                          className="picker-card-read-more"
                          onClick={e => toggleExpanded(e, ct.id)}
                          aria-expanded={expandedIds.has(ct.id)}
                        >
                          {expandedIds.has(ct.id) ? 'Show less' : 'Read more'}
                        </button>
                      )}
                    </>
                  )}
                </div>
                <div className="picker-card-arrow">
                  <i className="fas fa-chevron-right" />
                </div>
              </button>
            ))
          )}
        </div>
      )}

      {!isLoading && !error && selectedCaseType && (
        <div className="picker-grid">
          <button
            id="intakeMode-new"
            className="picker-card"
            onClick={() => handleSelectIntakeMode('new')}
            type="button"
          >
            <div className="picker-card-icon">
              <i className="fas fa-file-medical" />
            </div>
            <div className="picker-card-body">
              <h5 className="picker-card-name">Start a New Case</h5>
              <p className="picker-card-desc">
                Begin a brand-new case from a referral or intake packet. GuideSmart will generate the Initial Case
                Report as your first report.
              </p>
            </div>
            <div className="picker-card-arrow">
              <i className="fas fa-chevron-right" />
            </div>
          </button>

          <button
            id="intakeMode-ongoing"
            className="picker-card"
            onClick={() => handleSelectIntakeMode('ongoing')}
            type="button"
          >
            <div className="picker-card-icon">
              <i className="fas fa-history" />
            </div>
            <div className="picker-card-body">
              <h5 className="picker-card-name">Intake an Ongoing Case</h5>
              <p className="picker-card-desc">
                Bring in an active case that already has an Initial Case Report and/or Progress Reports. Upload your
                existing reports to automatically extract case data, number prior reports in chronological order, and
                set up your next Progress Report.
              </p>
            </div>
            <div className="picker-card-arrow">
              <i className="fas fa-chevron-right" />
            </div>
          </button>
        </div>
      )}
    </div>
  )
}

export default CaseTypePicker
